import { z } from 'zod'
import { Model } from '../../_kernel/architecture'

import { Money } from '../../_kernel/Money'
import { ID } from '../../_kernel/ID'
import { Url } from '../../_kernel/Url'
import { Market } from '../../shared/domain/Market'
import { Currency } from '../../_kernel/Currency'
import { Description } from '../../_kernel/Description'

export const InsuranceValidation = z.object({
  id: z.instanceof(ID, { message: '[Insurance] id is required' }),
  price: z.instanceof(Money, { message: '[Insurance] price is required' }),
  terms: z.instanceof(Url, { message: '[Insurance] terms URL is required' }),
  market: z.instanceof(Market, { message: '[Insurance] market is required' }),
  description: z.instanceof(Description).optional(),
})

export class Insurance extends Model {
  static create({ id, price, terms, market, description }: z.infer<typeof InsuranceValidation>) {
    InsuranceValidation.parse({ id, price, terms, market, description })
    return new Insurance(id, price, terms, market, description)
  }

  static fromJSON(json: ReturnType<Insurance['toJSON']>) {
    return Insurance.create({
      id: ID.create({ value: json.id }),
      price: Money.create({
        amount: json.price.amount,
        currency: Currency.create({ value: json.price.currency }),
      }),
      terms: Url.fromJSON(json.terms),
      market: Market.fromJSON({ value: json.market }),
      description: json.description ? Description.create({ value: json.description }) : undefined,
    })
  }

  static fromJSONOrUndefined(json: ReturnType<Insurance['toJSON']> | undefined) {
    try {
      if (!json) return undefined

      return Insurance.fromJSON(json)
    } catch {
      return undefined
    }
  }

  constructor(
    private readonly _id: ID,
    private readonly _price: Money,
    private readonly _terms: Url,
    private readonly _market: Market,
    private readonly _description?: Description
  ) {
    super()
  }

  get id() { return this._id.value } // prettier-ignore

  get price() { return this._price.toJSON() } // prettier-ignore

  get terms() { return this._terms.toJSON() } // prettier-ignore

  get market() { return this._market.value } // prettier-ignore

  toJSON() {
    return {
      id: this.id,
      price: this.price,
      terms: this.terms,
      market: this.market,
      ...(this._description && { description: this._description.value }),
    }
  }

  hasInsurance(): boolean {
    return !!this.price
  }

  getInsuranceTerms(): Url | undefined {
    return this._terms
  }

  getInsuranceDescription() {
    return this._description?.value ?? ''
  }

  getPriceFormatted(locale: string, market: Market): string | undefined {
    return this._price.toFormattedString(locale, market)
  }
}
